import { useState, useEffect, useRef, useCallback } from 'react'
import {
  Form,
  FormInstance,
  Row,
  Col,
  Input,
  InputNumber,
} from 'antd'
import useForm from '@hooks/useForm'
import type { IOrder } from '@/types/IOrder'
import { SearchSelect } from '@components/User/Form'
import { useTranslation } from 'react-i18next'
import { useGenericContext } from '@hooks/useGenericContext'
import { OrderContext } from '@contexts/OrderContext'
import FormUser from '@components/Company/FormUser'
import SelectDate from '../SelectDate'
import { removeNullValues } from '@helpers/removeNullValues'
import useTableContext from '@hooks/useTableContext'
import { FormOrderProps, SearchSelectCRMProps } from '@/types/IOrder'
import useFormCalculations from '@hooks/Order/useFormCalculations'
import useFormInitialization from '@hooks/Order/useFormInitialization'
import FormItems from '../FormItems'
import { formatOrderItem } from '@helpers/Order/formatOrderItem'
import CompanySelect from '@components/common/CompanySelect'
import DeviceSelectForAll from '@components/Ticket/DeviceSelectForAll'
import { SelectInput } from '@components/common/SelectInput'

const handleFinish = (values: IOrder, onFinish: (object: any) => void, action: string) => {
  const formattedValues = {
    ...removeNullValues(values),
    items: values.items?.map((item) => {
      const supportLevel = item.support_level ?? 'BASIC'

      const relatedProducts = item.order_request?.related_products ?? []
      const filteredRelatedProducts =
        action === 'edit'
          ? relatedProducts.map((rp: any) => ({
            product: { uuid: rp.product.uuid },
            items: rp.items?.map(formatOrderItem) ?? []
          }))
          : relatedProducts
            .filter((rp: any) => rp.pivot?.support_level === supportLevel)
            .map((rp: any) => ({
              product: { uuid: rp.product.uuid },
              items: rp.items?.map(formatOrderItem) ?? []
            }))

      return {
        product: { uuid: item.product.uuid, device: item.product?.device },
        order_request: {
          support_level: supportLevel,
          items: removeNullValues(item.order_request?.items?.map(formatOrderItem) ?? []),
          related_products: filteredRelatedProducts
        }
      }
    }) ?? []
  }
  onFinish(formattedValues)
}

const FormOrder = ({ data }: FormOrderProps) => {
  const { refreshTable } = useTableContext()
  const [formAnt] = Form.useForm()
  const formRef = useRef<FormInstance>(formAnt)
  const form = formRef.current
  const { action, numForm, onClose, setPrice } = useGenericContext(OrderContext)
  const { t } = useTranslation()
  const url = action === 'change' ? '/order/change' : '/order'
  const { onFinish, disabled, contextHolder, responseData } = useForm(
    action,
    onClose,
    url,
    data?.uuid
  )
  const {
    calculateSubtotal,
    debouncedCalculateAndSetPrices,
    subtotals,
    setSubtotals,
  } = useFormCalculations(form, setPrice);
  useFormInitialization(data, form, debouncedCalculateAndSetPrices);

  useEffect(() => {
    if (responseData?.uuid) {
      refreshTable('/order?sort=-created_at')
    }
  }, [responseData])

  const term_type = Form.useWatch('type_term', form) as string

  return (
    <Form
      form={form}
      disabled={disabled}
      layout="vertical"
      onFinish={(values) => handleFinish(values, onFinish, action)}
      id={numForm}
      onValuesChange={(_, allValues) => {
        debouncedCalculateAndSetPrices(allValues)
      }}
    >
      {contextHolder}
      <Row gutter={[16, 16]}>
        {action !== 'change' && !data?.device ?
          <>
            <SearchSelectCRM data={data} name="customer" form={form} />
            <SearchSelectCRM data={data} name="opportunity" form={form} />
          </>
          :
          <>
            <Col lg={8} xs={24}>
              <CompanySelect
                name="company"
                rules={[{ required: true, message: 'requiredItem' }]}
                initialValue={
                  data?.company ? { value: data.company.uuid, label: data.company.name } : undefined
                }
              />
            </Col>
            <Col lg={12} xs={24}>
              <DeviceSelectForAll name="device" />
            </Col>
          </>
        }
        <SelectInput
          span={6}
          label={t('TYPE_TERM')}
          constant="ORDER_TERM_DISCOUNT_TYPES"
          inputName='type_term'
          required={true}
          allowClear={false}
          selectFirstOption
          defaultValue={data?.type_term}
          data-cy="select-term"
        />
        <Col lg={6} xs={24}>
          <SelectDate term_type={term_type} defaultValue={data?.type_term} />
        </Col>
        <Col span={24}>
          <FormUser
            action={action}
            data={data?.contract_signatures}
            nameForm="contract_signatures"
            title="SIGNATURE"
          />
        </Col>
        <Col span={24}>
          <FormItems
            form={form}
            subtotals={subtotals}
            setSubtotals={setSubtotals}
            calculateSubtotal={calculateSubtotal}
            debouncedCalculateOrderPrice={debouncedCalculateAndSetPrices}
          />
        </Col>
        <InputPercent />
      </Row>
    </Form>
  )
}

const SearchSelectCRM = ({ data, name, form }: SearchSelectCRMProps) => {
  const [searchUrl, setSearchUrl] = useState(`crm/${name}?`)

  const oppositeName = name === 'customer' ? 'opportunity' : 'customer'
  const oppositeValue = Form.useWatch(oppositeName, form)

  const handleSearchUpdate = useCallback(() => {
    const queryParam = oppositeValue ? `${oppositeName}=${oppositeValue}&` : ''
    setSearchUrl(`crm/${name}?${queryParam}`)
  }, [oppositeName, oppositeValue, name])

  useEffect(() => {
    handleSearchUpdate()
  }, [oppositeValue, handleSearchUpdate])

  return (
    <Col lg={6} xs={24}>
      <SearchSelect
        initialValue={
          data?.[name] ? [{ value: data[name].uuid, label: data[name].name }] : []
        }
        require={true}
        name={name.toUpperCase()}
        searchUrl={searchUrl}
        onChange={handleSearchUpdate}
        allowClear
      />
    </Col>
  )
}

const InputPercent = () => {
  const { t } = useTranslation()

  return (
    <>
      <Col xs={24} lg={4}>
        <Form.Item name="discount_percents" label={t('DISCOUNT')}>
          <InputNumber addonAfter="%" min={0} max={100} />
        </Form.Item>
      </Col>
      <Col xs={24} lg={20}>
        <Form.Item name="discount_motive" label={t('DISCOUNT_MOTIVE')}>
          <Input.TextArea rows={2} autoSize={{ minRows: 2, maxRows: 4 }} />
        </Form.Item>
      </Col>
    </>
  )
}

export default FormOrder
